import { useState } from "react";
import { useAuth } from "../auth/Login";
import { useNavigate, useLocation } from "react-router-dom";
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Paper, 
  Avatar,
  CssBaseline,
  Fade,
  Zoom,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton
} from "@mui/material";
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { keyframes } from "@emotion/react";

// Subtle gradient animation
const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const LoginPage = () => {
  const [contactNumber, setContactNumber] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Get the redirect path from location state or default to dashboard
  const from = location.state?.from?.pathname || "/dashboard";

  // Validate phone number format (exactly 11 digits starting with 03)
  const isValidPhoneNumber = (phone) => {
    const phoneRegex = /^03\d{9}$/;
    return phoneRegex.test(phone) && phone.length === 11;
  };

  // Handle contact number input - only allow numbers
  const handleContactNumberChange = (e) => {
    const value = e.target.value;
    // Only allow numeric input and maximum 11 digits
    if (value === '' || (/^\d+$/.test(value) && value.length <= 11)) {
      setContactNumber(value);
    }
  };

  // Validate password (at least 8 characters)
  const isValidPassword = (pass) => {
    return pass.length >= 8;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    // Validate inputs
    if (!contactNumber) {
      setError("Please enter your contact number");
      return;
    }

    if (!isValidPhoneNumber(contactNumber)) {
      setError("Please enter a valid contact number (e.g., 03XXXXXXXXX)");
      return;
    }

    if (!password) {
      setError("Please enter your password");
      return;
    }

    if (!isValidPassword(password)) {
      setError("Password must be at least 8 characters long");
      return;
    }
    
    try {
      setIsLoading(true);
      const result = await login(contactNumber, password);
      
      if (result.success) {
        // Navigate to the intended destination
        navigate(from, { replace: true });
      } else {
        setError(result.message || "Login failed. Please check your credentials.");
      }
    } catch (err) {
      setError("Login failed. Please try again later.");
      console.error("Login error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Toggle password visibility
  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: 'linear-gradient(-45deg, #f5f7fa 0%, #c3cfe2 50%, #e2e8f0 100%)',
        backgroundSize: '400% 400%',
        animation: `${gradientShift} 8s ease infinite`,
      }}
    >
      <CssBaseline />
      <Zoom in timeout={500}>
        <Paper 
          elevation={6}
          sx={{ 
            p: 4, 
            width: { xs: '90%', sm: 400 },
            borderRadius: 3,
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            backdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            boxShadow: '0 8px 32px rgba(31, 38, 135, 0.15)',
          }}
        >
          <Fade in timeout={800}>
            <Avatar sx={{ 
              m: 1, 
              bgcolor: 'primary.main',
              width: 56, 
              height: 56,
              boxShadow: 3 
            }}>
              <LockOutlinedIcon fontSize="medium" />
            </Avatar>
          </Fade>
          <Typography 
            component="h1" 
            variant="h5"
            sx={{ 
              mt: 1,
              fontWeight: 600,
              letterSpacing: 0.5,
              color: 'text.primary'
            }}
          >
            Welcome Back
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ width: '100%', mt: 2 }}>
              {error}
            </Alert>
          )}
          
          <Box 
            component="form" 
            onSubmit={handleSubmit} 
            sx={{ 
              mt: 3, 
              width: '100%',
              '& .MuiTextField-root': { 
                my: 1.5,
                '& label.Mui-focused': {
                  color: 'primary.main',
                },
              }
            }}
          >
            <TextField
              fullWidth
              label="Contact Number"
              variant="outlined"
              placeholder="03XXXXXXXXX"
              autoFocus
              value={contactNumber}
              onChange={handleContactNumberChange}
              inputProps={{
                maxLength: 11,
                inputMode: 'numeric',
                pattern: '[0-9]*'
              }}
              InputProps={{
                sx: { borderRadius: 2 }
              }}
              helperText={`${contactNumber.length}/11 digits`}
              error={contactNumber.length > 0 && contactNumber.length !== 11}
            />
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? "text" : "password"}
              variant="outlined"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                sx: { borderRadius: 2 },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading}
              sx={{ 
                mt: 3, 
                mb: 2,
                py: 1.5,
                borderRadius: 2,
                fontSize: '1rem',
                fontWeight: 500,
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 1,
                },
                transition: 'all 0.2s ease',
              }}
            >
              {isLoading ? <CircularProgress size={24} color="inherit" /> : "Login"}
            </Button>
          </Box>
        </Paper>
      </Zoom>
    </Box>
  );
};

export default LoginPage;